import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useSupportWhatsApp, fmtPhone, waLink } from '@/lib/support';
import { useAuth } from '@/lib/auth';

export const MAX_RECHARGE_INR = 15000;
export const LimitContext = createContext({ used: 0, remaining: MAX_RECHARGE_INR });
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const UpgradeLimitDialog = ({ open, onOpenChange, amount }) => {
    const { user } = useAuth();
    const wa = useSupportWhatsApp();
    const msg = `Hello Eagle Eyes Services, please increase my daily recharge limit above ₹15,000.\n\nName: ${user?.name || ''}\nEmail: ${user?.email || ''}\nWhatsApp: ${user?.whatsapp || user?.mobile || ''}${amount ? `\nAmount I want to recharge: ₹${Number(amount).toLocaleString('en-IN')}` : ''}\n\nThank you.`;
    const href = waLink(wa, msg);
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="upgrade-limit-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-rose-600" /> Daily recharge limit reached</DialogTitle>
                    <DialogDescription>Online recharges are limited to <strong>₹15,000 per 24 hours</strong>; the limit restores 24 hours after your last recharge. If you need a higher limit, ask us on WhatsApp to upgrade your account.</DialogDescription>
                </DialogHeader>
                <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Our WhatsApp</div>
                    <div className="font-display text-lg font-semibold tabular-nums" data-testid="upgrade-limit-whatsapp-number">{fmtPhone(wa)}</div>
                    <div className="mt-2 text-xs text-muted-foreground">Message that will be sent:</div>
                    <pre className="mt-1 whitespace-pre-wrap font-sans text-xs text-foreground/80" data-testid="upgrade-limit-message">{msg}</pre>
                </div>
                <p className="text-[11px] text-muted-foreground">Any payment over the limit is refunded under the payment gateway's guidelines, and the money reaches your original payment source in 5–7 working days.</p>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="upgrade-limit-close">Close</Button>
                    <Button asChild className="bg-[#25D366] hover:bg-[#1EBE5A] text-white" data-testid="upgrade-limit-send-whatsapp">
                        <a href={href} target="_blank" rel="noopener noreferrer"><MessageCircle className="h-4 w-4 mr-1.5" /> Send message on WhatsApp</a>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const LimitWarning = ({ testid = 'recharge-limit-warning', amount }) => {
    const [open, setOpen] = useState(false);
    const { used, remaining, resetsAt } = useContext(LimitContext);
    const shown = useRef(false);
    useEffect(() => { if (!shown.current) { shown.current = true; setOpen(true); } }, []);
    return (
        <>
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-[13px] text-rose-800" role="alert" data-testid={testid}>
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                    <strong>Daily limit is ₹15,000.</strong> You've recharged {inr(used)} in the last 24 hours, so you can pay up to <strong data-testid={`${testid}-remaining`}>{inr(remaining)}</strong> more right now.{resetsAt && <> Full limit restores on <strong data-testid={`${testid}-resets-at`}>{new Date(resetsAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</strong> (24 hours after your last recharge).</>}
                    Any payment over the limit is refunded under the payment gateway's guidelines, and the money reaches your original payment source in 5–7 working days.{' '}
                    <a href="/policies/refund" target="_blank" rel="noreferrer" className="underline font-medium">Refund policy</a>
                    <div className="mt-2">
                        <Button type="button" size="sm" variant="outline" className="h-7 border-rose-300 text-rose-800 hover:bg-rose-100" onClick={() => setOpen(true)} data-testid={`${testid}-request-upgrade`}>
                            <MessageCircle className="h-3.5 w-3.5 mr-1" /> Request higher limit
                        </Button>
                    </div>
                </div>
            </div>
            <UpgradeLimitDialog open={open} onOpenChange={setOpen} amount={amount} />
        </>
    );
};
